import { http, HttpResponse } from 'msw';
import type { RequestHandler } from 'msw';

import { DemoProfile, Usertype } from '../types';
import {
    completeOnboardingTask,
    getCompletedOnboardingTaskNames,
    getOnboardingStep,
    getOnboardingStepTaskNames,
} from '../onboardingProgress';
import { CONTENT } from '../utils';

import {
    GroupActionTypes,
    MemberTaskFeedItemTypes,
    SubmitAction,
    SubmitActionTypes,
    Task,
    TaskGroupSummary,
    TaskPriorities,
    TaskStatuses,
    type FormField,
} from 'modules/Tasks/types';
import { getCurrentQTE } from 'modules/Tax/utils/dates';

/**
 * Every `/v2/members/tasks/*` mock the demo needs, in one place — the member task framework
 * (`src/modules/Tasks`) is what renders the dashboard carousel, the `/tasks` page, and the
 * group drawer, so the demo feeds it real framework payloads instead of bespoke task cards.
 *
 * Two audiences share these endpoints:
 *   - **Activated members** — the ranked priority feed (`/feed/`), the flat ungrouped list, and one
 *     task group. Task copy is marketing-editable in `demo/content/{scorp,llc}.json` (`tasks` +
 *     `task_group`); this module only turns those blobs into the framework's wire shapes.
 *   - **Onboarding (launchpad) members** — the operator-task call (`?task_template_names=…`) that
 *     drives the launchpad. Launchpad tasks deliberately carry no `location`, so they never leak
 *     into the carousel / Tasks page (see `isFrameworkTask`).
 *
 * Completions are in-memory: finishing a task drops it from every surface until the page reloads.
 */

// ---- Content shapes (demo/content/*.json) ----

type DemoTaskAction =
    | { type: 'redirect'; label: string; url: string }
    | { type: 'upload'; label: string; document_type_id: string }
    | { type: 'form'; label: string; fields: FormField[] };

interface DemoTaskBlob {
    name: string;
    display_name: string;
    instructions: string;
    /** Product area — drives the Tasks-page area filter (`taxes`/`accounting`/`invoicing`/…). */
    area: string;
    /** Pages the task surfaces on. Must be non-empty or the framework renders nothing. */
    location: string[];
    priority?: 'high' | 'normal';
    /** Due date relative to today, so a demo months from now still looks current. Negative = overdue. */
    due_in_days: number;
    action: DemoTaskAction;
    /** Long-form markdown shown inside the upload/form surface. */
    notes?: string;
}

interface DemoTaskGroupBlob {
    name: string;
    display_name: string;
    description: string;
    area: string;
    location: string[];
    priority?: 'high' | 'normal';
    tasks: DemoTaskBlob[];
}

interface TasksContentBlob {
    tasks?: DemoTaskBlob[];
    task_group?: DemoTaskGroupBlob;
}

// ---- Ids + session state ----

const DEMO_GROUP_ID = 'demo-group-monthly-bank-statements';
const taskId = (name: string) => `demo-task-${name}`;

// Tasks the visitor completed this session. Read by every list handler so a finished task drops off
// the carousel and the Tasks page the way it does in prod. Resets on page refresh.
const completedTaskNames = new Set<string>();

// ---- Builders ----

/** Midnight UTC `due_in_days` from today — the same "calendar day" shape the backend sends. */
function dueDate(daysFromNow: number): string {
    const date = new Date();
    date.setUTCHours(0, 0, 0, 0);
    date.setUTCDate(date.getUTCDate() + daysFromNow);
    return date.toISOString();
}

function priorityValue(priority: DemoTaskBlob['priority']): number {
    return priority === 'high' ? TaskPriorities.HIGH : TaskPriorities.NORMAL;
}

/**
 * `Q3`-style label from the same `getCurrentQTE()` the Taxes pages use, so copy can't go stale.
 * Not keyed `quarter` — `UploadModal` forwards `context.quarter` into the upload meta.
 */
function quarterLabel(): string {
    const [, quarter] = getCurrentQTE();
    return `Q${quarter}`;
}

function submitAction(action: DemoTaskAction): SubmitAction {
    if (action.type === 'redirect') {
        return { type: SubmitActionTypes.REDIRECT, label: action.label, url: action.url };
    }
    if (action.type === 'upload') {
        return { type: SubmitActionTypes.UPLOAD, label: action.label };
    }
    return { type: SubmitActionTypes.FORM, label: action.label };
}

/** Action-specific `context` the built-in submit surfaces read (upload document type, form fields). */
function actionContext(action: DemoTaskAction): Record<string, unknown> {
    if (action.type === 'upload') {
        return { document_type_id: action.document_type_id };
    }
    if (action.type === 'form') {
        return { fields: action.fields };
    }
    return {};
}

function buildTask(blob: DemoTaskBlob, profile: DemoProfile, groupId: string | null = null): Task {
    return {
        id: taskId(blob.name),
        status: TaskStatuses.READY,
        priority: priorityValue(blob.priority),
        effective_area: blob.area,
        context: {
            // `{organization}` / `{first_name}` / `{quarter_label}` in any copy field resolve through
            // the framework's own interpolation, so the JSON stays free of per-persona strings.
            organization: profile.organization,
            first_name: profile.first_name,
            quarter_label: quarterLabel(),
            ...(blob.notes ? { notes: blob.notes } : {}),
            ...actionContext(blob.action),
        },
        task_template: {
            id: `demo-tpl-${blob.name}`,
            name: blob.name,
            display_name: blob.display_name,
            instructions: blob.instructions,
            submit_action: submitAction(blob.action),
            is_member_task: true,
            location: blob.location,
            area: blob.area,
            has_custom_component: false,
        },
        task_type: blob.name,
        source: 'demo',
        task_labels: [],
        due_date: dueDate(blob.due_in_days),
        flagged: false,
        is_in_sla: true,
        effective_sla_hours: 72,
        created_at: dueDate(blob.due_in_days - 14),
        updated_at: dueDate(0),
        completed_at: null,
        waiting_on: null,
        group_id: groupId,
    };
}

function buildGroup(blob: DemoTaskGroupBlob, children: Task[], profile: DemoProfile): TaskGroupSummary {
    const dueDates = children.map((task) => task.due_date).sort();
    return {
        id: DEMO_GROUP_ID,
        group_template: {
            id: `demo-group-tpl-${blob.name}`,
            name: blob.name,
            display_name: blob.display_name,
            description: blob.description,
            location: blob.location,
            area: blob.area,
            // `expand` → the framework's own TaskGroupDrawer + GroupTaskList, no custom component.
            group_action: { type: GroupActionTypes.EXPAND, label: 'View tasks' },
            has_custom_component: false,
        },
        context: { organization: profile.organization, count: children.length },
        tasks_count: children.length,
        next_due_date: dueDates[0] ?? null,
        priority: priorityValue(blob.priority),
        created_at: dueDate(-30),
        updated_at: dueDate(0),
    };
}

// ---- Launchpad (onboarding) tasks ----

// Subtasks that deep-link into a real flow. The cta_url both navigates and makes the row render a
// "Start" button (without it, the row falls back to a misleading "In progress" chip).
const ONBOARDING_TASK_CTA_URLS: Record<string, string> = {
    // Formation: the real entity formation flow.
    complete_application: '/onboarding/entity-formation',
    // S Corp election: real flow, mocked classify/extract pipeline.
    s_corp_election_upload: '/onboarding/scorp-election/info',
    // Business verification: the demo-specific flow (DemoBusinessVerification).
    business_verification_upload_ein: '/onboarding/business-verification',
    business_verification_upload_scorp_confirmation_letter: '/onboarding/business-verification',
    business_verification_upload_articles_of_organization_middesk: '/onboarding/business-verification',
};

// Third-party-iframe steps (Relay/Gusto/Coverdash + accounting) can't run in the demo, so they're
// shown locked: forced BLOCKED → a disabled lock button. The cta_url only exists so that button
// renders; it never navigates (blocked rows are disabled).
const LOCKED_ONBOARDING_TASK_NAMES = new Set([
    'insurance_setup',
    'open_connect_business_bank_account',
    'complete_accounting_setup',
    'complete_payroll_setup',
]);
const LOCKED_STEP_CTA = '/dashboard';

// One operator task. `completed` → checkmark, locked → BLOCKED (disabled lock button), else pending.
// Every task is returned (not just completed) so existence-based validators don't hide rows.
function buildOnboardingTask(name: string, completed: boolean) {
    const isLocked = !completed && LOCKED_ONBOARDING_TASK_NAMES.has(name);
    const ctaUrl = ONBOARDING_TASK_CTA_URLS[name] ?? (isLocked ? LOCKED_STEP_CTA : undefined);
    let status = 'pending';
    if (completed) {
        status = 'completed';
    } else if (isLocked) {
        status = 'blocked';
    }
    return {
        id: taskId(name),
        status,
        context: ctaUrl ? { cta_url: ctaUrl } : {},
        task_template: { name, display_name: name, instructions: '', resources: {} },
    };
}

// All of the journey's tasks: first `step` rows (and any flow-completed rows) done, the rest pending.
// `state` selects the launchpad sequence, which differs in a few states.
function buildOnboardingTasks(journey: Usertype, step: number, state: string) {
    const completed = getCompletedOnboardingTaskNames(journey, step, state);
    return getOnboardingStepTaskNames(journey, state)
        .flat()
        .map((name) => buildOnboardingTask(name, completed.has(name)));
}

// ---- Handlers ----

/** A ranked entry plus the locations the `location` surface filters on (not part of the wire shape). */
interface RankedEntry {
    item_type: string;
    task: Task | null;
    group: TaskGroupSummary | null;
    priority: number;
    due_date: string;
    locations: string[];
}

/**
 * Ranked like the backend's feed serializer: higher priority first, then earliest due date. The
 * carousel never re-sorts, so this order is what a prospect sees.
 */
function rankFeedItems(tasks: Task[], group: TaskGroupSummary | null): RankedEntry[] {
    const entries: RankedEntry[] = tasks.map((task) => ({
        item_type: MemberTaskFeedItemTypes.TASK,
        task,
        group: null,
        priority: task.priority,
        due_date: task.due_date,
        locations: task.task_template.location,
    }));
    if (group) {
        entries.push({
            item_type: MemberTaskFeedItemTypes.GROUP,
            task: null,
            group,
            priority: group.priority,
            due_date: group.next_due_date ?? '',
            locations: group.group_template.location,
        });
    }
    return entries.sort((a, b) => b.priority - a.priority || a.due_date.localeCompare(b.due_date));
}

/** The serializer's shape: item_type + the nested task/group, plus the ranking fields it echoes. */
function toFeedEntry({ locations, ...entry }: RankedEntry) {
    return entry;
}

export default function buildMemberTaskHandlers(profile: DemoProfile, stateCode: string): RequestHandler[] {
    const isOnboarding = profile.view === 'launchpad';
    const content = CONTENT[profile.entity_type] as TasksContentBlob;

    // Built per request so `due_in_days` stays relative to *now* and completions are reflected.
    const activeTasks = () =>
        (content.tasks ?? [])
            .filter((blob) => !completedTaskNames.has(blob.name))
            .map((blob) => buildTask(blob, profile));

    const activeGroupTasks = () =>
        (content.task_group?.tasks ?? [])
            .filter((blob) => !completedTaskNames.has(blob.name))
            .map((blob) => buildTask(blob, profile, DEMO_GROUP_ID));

    // A group with no tasks left disappears, matching prod's "empty group drops off the feed".
    const activeGroup = () => {
        const blob = content.task_group;
        const children = activeGroupTasks();
        return blob && children.length > 0 ? buildGroup(blob, children, profile) : null;
    };

    return [
        // The server-ranked carousel feed. Must be registered before the `:id` handler below, which
        // would otherwise swallow `/feed/` and return null (crashing the response unwrap).
        http.get('*/v2/members/tasks/feed/', ({ request }) => {
            if (isOnboarding) {
                return HttpResponse.json({ total_items: 0, items: [] });
            }
            const params = new URL(request.url).searchParams;
            const limit = Number(params.get('limit')) || 5;
            const location = params.get('location');
            const ranked = rankFeedItems(activeTasks(), activeGroup()).filter(
                (item) => !location || item.locations.includes(location)
            );
            return HttpResponse.json({
                // Pre-limit total — the carousel turns the overflow into its "N tasks remaining" card.
                total_items: ranked.length,
                items: ranked.slice(0, limit).map(toFeedEntry),
            });
        }),

        // Group summaries for the Tasks page (the carousel gets its group from /feed/).
        http.get('*/v2/members/tasks/groups/', ({ request }) => {
            const group = isOnboarding ? null : activeGroup();
            const location = new URL(request.url).searchParams.get('location');
            if (!group || (location && !group.group_template.location.includes(location))) {
                return HttpResponse.json([]);
            }
            return HttpResponse.json([group]);
        }),

        http.get('*/v2/members/tasks/', ({ request }) => {
            const params = new URL(request.url).searchParams;

            // Launchpad/operator-tasks call (has ?task_template_names) — drives the launchpad items.
            if (params.has('task_template_names')) {
                return HttpResponse.json(
                    isOnboarding ? buildOnboardingTasks(profile.journey, getOnboardingStep(), stateCode) : []
                );
            }
            // No activated-member tasks during onboarding (the launchpad is the guidance).
            if (isOnboarding) {
                return HttpResponse.json([]);
            }
            // Group drill-in (the drawer's task list).
            const groupId = params.get('group_id');
            if (groupId) {
                return HttpResponse.json(groupId === DEMO_GROUP_ID ? activeGroupTasks() : []);
            }
            // `ungrouped=true` (the Tasks page) keeps grouped tasks out of the flat list so they
            // don't render twice — once inline and once inside their group card.
            const location = params.get('location');
            const tasks =
                params.get('ungrouped') === 'true' ? activeTasks() : [...activeTasks(), ...activeGroupTasks()];
            return HttpResponse.json(
                tasks.filter((task) => !location || task.task_template.location.includes(location))
            );
        }),

        http.get('*/v2/members/tasks/:id/', () => HttpResponse.json(null)),

        // Completing a task drops it from the carousel, the Tasks page, and its group.
        http.post('*/v2/members/tasks/:id/status/', async ({ params, request }) => {
            const id = String(params.id);
            const body = (await request.json().catch(() => ({}))) as { status?: string };
            const name = id.replace(/^demo-task-/, '');
            // Only an explicit COMPLETED finishes the task ("Start" posts IN_PROGRESS).
            if (body.status === TaskStatuses.COMPLETED) {
                completedTaskNames.add(name);
                // Launchpad flows advance per step, so tell the onboarding tracker too.
                completeOnboardingTask(name);
            }
            return HttpResponse.json({ ok: true, status: body.status ?? 'COMPLETED' });
        }),
    ];
}
